import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { filter } from 'rxjs/operators';
import { StorageService } from './saferoadsph/services/storage.service';

declare let gtag: Function;

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    providers: [ConfirmationService]
})
export class AppComponent implements OnInit, OnDestroy {
    private readonly idleTimeoutMs = 30 * 60 * 1000;
    private readonly logoutGracePeriodMs = 10 * 1000;

    private idleTimerId: number | null = null;
    private forceLogoutTimerId: number | null = null;
    private idlePromptShown = false;

    menuMode = 'static';

    lightMenu = false;

    inputStyle = 'outlined';

    ripple = false;

    constructor(
        private primengConfig: PrimeNGConfig,
        private router: Router,
        private storageService: StorageService,
        private confirmationService: ConfirmationService
    ) {
    }

    ngOnInit() {
        this.setUpAnalytics();
        this.primengConfig.ripple = true;
        this.ripple = true;

        this.startIdleTimer();
    }

    ngOnDestroy(): void {
        this.clearIdleTimer();
        this.clearForceLogoutTimer();
    }

    @HostListener('document:mousemove')
    @HostListener('document:keydown')
    @HostListener('document:click')
    @HostListener('document:scroll')
    @HostListener('document:touchstart')
    onUserActivity(): void {
        if (!this.hasActiveSession() || this.idlePromptShown) {
            return;
        }

        this.startIdleTimer();
    }

    setUpAnalytics() {
        this.router.events.pipe(filter(event => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                gtag('event', 'page_view', {
                    page_path: event.urlAfterRedirects
                });

                if (this.hasActiveSession() && !this.idlePromptShown) {
                    this.startIdleTimer();
                }
            });
    }

    private hasActiveSession(): boolean {
        return !!localStorage.getItem('token');
    }

    private startIdleTimer(): void {
        this.clearIdleTimer();

        if (!this.hasActiveSession()) {
            return;
        }

        this.idleTimerId = window.setTimeout(() => {
            this.showIdlePrompt();
        }, this.idleTimeoutMs);
    }

    private clearIdleTimer(): void {
        if (this.idleTimerId !== null) {
            window.clearTimeout(this.idleTimerId);
            this.idleTimerId = null;
        }
    }

    private clearForceLogoutTimer(): void {
        if (this.forceLogoutTimerId !== null) {
            window.clearTimeout(this.forceLogoutTimerId);
            this.forceLogoutTimerId = null;
        }
    }

    private showIdlePrompt(): void {
        if (this.idlePromptShown || !this.hasActiveSession()) {
            return;
        }

        this.idlePromptShown = true;
        this.clearForceLogoutTimer();

        this.forceLogoutTimerId = window.setTimeout(() => {
            this.forceLogout();
        }, this.logoutGracePeriodMs);

        this.confirmationService.confirm({
            key: 'idle-session-timeout',
            header: 'Session Timeout',
            message: 'Your session has been idle for the past 30 minutes. Would you like to continue? Otherwise, you will be automatically logged out in 10 seconds.',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Continue',
            rejectLabel: 'Logout Now',
            closeOnEscape: false,
            dismissableMask: false,
            accept: () => {
                this.idlePromptShown = false;
                this.clearForceLogoutTimer();
                this.startIdleTimer();
            },
            reject: () => {
                this.forceLogout();
            }
        });
    }

    private forceLogout(): void {
        if (!this.hasActiveSession()) {
            this.idlePromptShown = false;
            this.clearIdleTimer();
            this.clearForceLogoutTimer();
            return;
        }

        this.idlePromptShown = false;
        this.clearIdleTimer();
        this.clearForceLogoutTimer();
        this.confirmationService.close();

        this.storageService.clear().then(() => {
            this.router.navigate(['/login']);
        });
    }

}
